import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import ReactPlaceholder from 'react-placeholder/lib';
import classNames from 'clsx';
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import RadioboxMarked from 'mdi-material-ui/RadioboxMarked';

import { VehicleTypeEnum, FormsEnum } from 'app/shared/enums';
import { useRadioGroupControl } from 'app/shared/hooks/controls/useRadioGroupControl';
import { DictionaryEntry } from 'app/services/dict.service';
import { VehicleTypeControlProps } from './VehicleTypeControl.interfaces';
import { useStyles } from './VehicleTypeControl.styles';
import { ControlContainer } from 'app/components/ControlContainer';
import { ReactComponent as CarIcon } from 'app/assets/icons/car-front.svg';
import { ReactComponent as MotorcycleIcon } from 'app/assets/icons/motorcycle.svg';
import { onVehicleTypeChange } from 'app/store/thunks/onVehicleTypeChange';
import { InputPlaceholder } from 'app/components/InputPlaceholder';

const vehicleTypeIconConfig = {
  [VehicleTypeEnum.Car]: CarIcon,
  [VehicleTypeEnum.Motorcycle]: MotorcycleIcon,
};

export default function VehicleTypeControl(props: VehicleTypeControlProps) {
  const classes = useStyles();
  const [readonly, setReadonly] = useState<boolean>(false);

  const { formName, input, isLoading, label, tooltip, width } = props;

  const dispatch = useDispatch();
  const {
    error,
    handleChange,
    handleClick,
    renderRadioButtons,
  } = useRadioGroupControl(props);

  const renderLabel = (entry: DictionaryEntry) => {
    const { key, value } = entry;

    const Icon = vehicleTypeIconConfig[key.toString()];

    return (
      <>
        {Icon && <Icon className={classes.icon} aria-hidden={'true'} />}
        {value.toString()}
      </>
    );
  };

  const onValueChange = (value: VehicleTypeEnum) => {
    if (formName === FormsEnum.CalculationForm) {
      dispatch(onVehicleTypeChange(value));
    }

    handleChange(value);
  };

  const renderRadioButton = (entry: DictionaryEntry) => {
    const isActive = entry.key.toString() === input.value.toString();

    return (
      <FormControlLabel
        key={entry.key.toString()}
        label={renderLabel(entry)}
        value={entry.key.toString()}
        tabIndex={0}
        onKeyDown={(e) => {
          if (
            e.target === e.currentTarget &&
            (e.key === 'Enter' || e.key === ' ')
          ) {
            e.preventDefault();
            if (readonly) {
              return;
            }
            onValueChange(entry.key.toString() as VehicleTypeEnum);
            setReadonly(true);
          }
        }}
        control={
          <Radio
            onClick={handleClick}
            color={'primary'}
            checkedIcon={<RadioboxMarked />}
            inputProps={{
              tabIndex: -1,
            }}
          />
        }
        classes={{
          root: classNames(classes.tile, 'focusVisibleOutline', {
            [classes.tileSelected]: isActive,
            [classes.error]: error,
          }),
          label: classes.label,
        }}
      />
    );
  };

  return (
    <ReactPlaceholder
      ready={!isLoading}
      customPlaceholder={<InputPlaceholder width={width} />}
    >
      <ControlContainer inputId={input.name} tooltip={tooltip} width={'xl'}>
        <FormControl margin={'dense'} className={classes.formControl}>
          <RadioGroup
            aria-label={label}
            name={input.name}
            value={input.value}
            onChange={(event, value) => {
              if (readonly) {
                return;
              }
              onValueChange(value as VehicleTypeEnum);
              setReadonly(true);
            }}
            className={classes.radioGroup}
          >
            {renderRadioButtons(renderRadioButton)}
          </RadioGroup>
        </FormControl>
      </ControlContainer>
    </ReactPlaceholder>
  );
}
